"use client";

import { useState } from "react";
import { Calendar } from "lucide-react";

export default function VendorUserAccountForm() {
  // Form state
  const [selectedMerchant, setSelectedMerchant] = useState("Cultural Triangle Authority");
  const [selectedRole, setSelectedRole] = useState("Merchant Admin");
  const [accessStatus, setAccessStatus] = useState("Active");
  const [validFrom, setValidFrom] = useState("2025-04-17");
  const [validTo, setValidTo] = useState("");
  const [noExpiry, setNoExpiry] = useState(true);

  // Branch access state
  const [branchAccess, setBranchAccess] = useState({
    "Sigiriya Office": true,
    "Anuradhapura Office": true,
    "Polonnaruwa Office": false,
  });

  // Permissions state
  const [permissions, setPermissions] = useState({
    "View Reports": true,
    "Manage Bookings": true,
    "Edit Profile": false,
    "Manage Users": false,
    "Financial Access": false,
    "System Settings": false,
  });

  // Calendar visibility state
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);

  // Toggle branch access
  const toggleBranchAccess = (branch) => {
    setBranchAccess({
      ...branchAccess,
      [branch]: !branchAccess[branch],
    });
  };

  // Toggle permission
  const togglePermission = (permission) => {
    setPermissions({
      ...permissions,
      [permission]: !permissions[permission],
    });
  };

  // Toggle no expiry
  const toggleNoExpiry = () => {
    setNoExpiry(!noExpiry);
    if (!noExpiry) {
      setValidTo("");
    }
  };

  // Format date for display (YYYY-MM-DD to DD-MM-YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${year}-${month}-${day}`;
  };

  // Handle save
  const handleSave = () => {
    // Add your save logic here
    console.log("Saving form data:", {
      selectedMerchant,
      selectedRole,
      accessStatus,
      validFrom,
      validTo,
      noExpiry,
      branchAccess,
      permissions,
    });
    alert("Form data saved successfully!");
  };

  // Calendar component
  const DatePicker = ({ onSelect, onClose, currentValue }) => {
    const today = new Date();
    const initialDate = currentValue ? new Date(currentValue) : today;

    const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth());
    const [currentYear, setCurrentYear] = useState(initialDate.getFullYear());
    const [currentDay, setCurrentDay] = useState(initialDate.getDate());

    // Generate days for current month
    const getDaysInMonth = (year, month) => {
      return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year, month) => {
      return new Date(year, month, 1).getDay();
    };

    const renderCalendarDays = () => {
      const daysInMonth = getDaysInMonth(currentYear, currentMonth);
      const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

      const days = [];

      // Empty cells for days before first day of month
      for (let i = 0; i < firstDay; i++) {
        days.push(<div key={`empty-${i}`} className="w-8 h-8"></div>);
      }

      // Days of the month
      for (let day = 1; day <= daysInMonth; day++) {
        const isSelected =
          day === currentDay && today.getMonth() === currentMonth && today.getFullYear() === currentYear;

        days.push(
          <div
            key={day}
            onClick={() => handleSelectDay(day)}
            className={`w-8 h-8 flex items-center justify-center rounded cursor-pointer hover:bg-blue-100 ${
              isSelected ? "bg-blue-500 text-white" : ""
            }`}
          >
            {day}
          </div>
        );
      }

      return days;
    };

    const handleSelectDay = (day) => {
      setCurrentDay(day);
      const selectedDate = new Date(currentYear, currentMonth, day);
      const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      onSelect(formattedDate);
      onClose();
    };

    const prevMonth = () => {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    };

    const nextMonth = () => {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    };

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div className="absolute top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={prevMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            &lt;
          </button>
          <div className="font-semibold">
            {monthNames[currentMonth]} {currentYear}
          </div>
          <button
            onClick={nextMonth}
            className="p-1 hover:bg-gray-100 rounded"
          >
            &gt;
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div key={day} className="w-8 h-8 flex items-center justify-center text-sm font-medium text-gray-600">
              {day}
            </div>
          ))}
          {renderCalendarDays()}
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button
            onClick={() => {
              const today = new Date();
              const formattedToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
              onSelect(formattedToday);
              onClose();
            }}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Today
          </button>
          <button onClick={onClose} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Sample data for dropdowns
  const merchants = ["Cultural Triangle Authority", "Ceylon Tourism Bureau", "Kandy Heritage Society"];
  const roles = ["Merchant Admin", "Branch Manager", "Staff", "Viewer"];
  const statusOptions = ["Active", "Inactive", "Pending"];

  // Branch data
  const branches = [
    { name: "Sigiriya Office", location: "Sigiriya, Central Province" },
    { name: "Anuradhapura Office", location: "Anuradhapura, North Central" },
    { name: "Polonnaruwa Office", location: "Polonnaruwa, North Central" },
  ];

  // Permissions data
  const permissionsList = [
    { name: "View Reports", description: "Access to view reports and analytics" },
    { name: "Manage Bookings", description: "Create, edit, and cancel bookings" },
    { name: "Edit Profile", description: "Modify merchant profile information" },
    { name: "Manage Users", description: "Add, edit, and remove user accounts" },
    { name: "Financial Access", description: "View financial data and transactions" },
    { name: "System Settings", description: "Configure system-level settings" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Vendor User Account</h1>
        <p className="text-gray-600">
          Creating a user with limited access to specific merchant entity and its destinations
        </p>
      </div>

      {/* Merchant Assignment Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Primary Merchant Assignment</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mb-6">
          {/* Associated Merchant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Associated Merchant*</label>
            <div className="relative">
              <select
                value={selectedMerchant}
                onChange={(e) => setSelectedMerchant(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                {merchants.map((merchant) => (
                  <option key={merchant} value={merchant}>
                    {merchant}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Merchant Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Merchant Role*</label>
            <div className="relative">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Access Status and Valid From */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mb-6">
          {/* Access Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Access Status*</label>
            <div className="relative">
              <select
                value={accessStatus}
                onChange={(e) => setAccessStatus(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Valid From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Valid From*</label>
            <div className="relative">
              <input
                type="text"
                value={formatDateForDisplay(validFrom)}
                readOnly
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white cursor-pointer"
              />
              <div
                className="absolute inset-y-0 right-0 flex items-center px-3 cursor-pointer text-gray-400 hover:text-blue-500"
                onClick={() => setShowFromCalendar(!showFromCalendar)}
              >
                <Calendar size={14} />
              </div>
              {showFromCalendar && (
                <DatePicker
                  onSelect={(date) => setValidFrom(date)}
                  onClose={() => setShowFromCalendar(false)}
                  currentValue={validFrom}
                />
              )}
            </div>
          </div>
        </div>

        {/* Date Selection Fields */}
        <div className="max-w-md">
          {/* Valid Till */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Valid Till</label>
            <div className="relative">
              <input
                type="text"
                value={formatDateForDisplay(validTo)}
                readOnly
                disabled={noExpiry}
                placeholder="Select date"
                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg ${
                  noExpiry ? "bg-gray-100 cursor-not-allowed" : "bg-white cursor-pointer"
                }`}
              />
              <div
                className={`absolute inset-y-0 right-0 flex items-center px-3 ${
                  noExpiry ? "cursor-not-allowed text-gray-300" : "cursor-pointer text-gray-400 hover:text-blue-500"
                }`}
                onClick={() => !noExpiry && setShowToCalendar(!showToCalendar)}
              >
                <Calendar size={14} />
              </div>
              {showToCalendar && !noExpiry && (
                <DatePicker
                  onSelect={(date) => setValidTo(date)}
                  onClose={() => setShowToCalendar(false)}
                  currentValue={validTo}
                />
              )}
            </div>
            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                id="noExpiry"
                checked={noExpiry}
                onChange={toggleNoExpiry}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="noExpiry" className="ml-2 text-sm text-gray-700">
                No Expiry
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Branch Access Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Merchant Branch Access</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Branch Name
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Location
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Access
                </th>
              </tr>
            </thead>
            <tbody>
              {branches.map((branch) => (
                <tr key={branch.name} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4 text-sm font-medium text-gray-900">{branch.name}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">{branch.location}</td>
                  <td className="py-4 px-4">
                    <div
                      className={`w-10 h-6 rounded-full cursor-pointer transition-colors flex items-center justify-center ${
                        branchAccess[branch.name] ? "bg-green-500" : "bg-gray-300"
                      }`}
                      onClick={() => toggleBranchAccess(branch.name)}
                    >
                      {branchAccess[branch.name] && (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Access and Permissions Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Entity User Access & Permissions</h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Permission
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Description
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                  Access
                </th>
              </tr>
            </thead>
            <tbody>
              {permissionsList.map((permission) => (
                <tr key={permission.name} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4 text-sm font-medium text-gray-900">{permission.name}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">{permission.description}</td>
                  <td className="py-4 px-4">
                    <div
                      className={`w-10 h-6 rounded-full cursor-pointer transition-colors flex items-center justify-center ${
                        permissions[permission.name] ? "bg-green-500" : "bg-gray-300"
                      }`}
                      onClick={() => togglePermission(permission.name)}
                    >
                      {permissions[permission.name] && (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Save Button */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg shadow-md transition-all duration-200 ease-in-out transform hover:scale-105 flex items-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            Save Change
          </button>
        </div>
      </div>
    </div>
  );
}